/* eslint-disable camelcase */
import React, {useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextareaAutosize,
  FormControl,
  Grid,
  Select,
  MenuItem,
  ListItemText,
  FormHelperText,
  makeStyles,
  Chip,
  Link
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {Controller, useForm} from 'react-hook-form';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {
  getAllCCMUsersOfClinicGroup,
  getCCMLogReasons,
  updatePrevFollowUpNotesLog,
  updateStopTimerPatientLog
} from '@shared/services/CCMServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
// import moment from 'moment';
import CCMEnrollementDialogUtils from 'src/modules/admin/modules/ChronicCareManagement/module-components/EnrollCCMDialog/CCMDialog/CCMEnrollementDialogUtils';
import {
  gridStyle,
  spaceBottomStyle
} from 'src/modules/admin/modules/ChronicCareManagement/module-components/EnrollCCMDialog/CCMDialog/CCMEnrollmentDialogStyle';
import {useDispatch, useSelector} from 'react-redux';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import Storage from 'src/utils/Storage';
import {
  AppPermissions,
  CCMConstants,
  FOLLOW_UP_NOTES_LOG_STATUS,
  SEARCH_DEBOUNCE_DELAY
} from '@shared/constants/Mapping';
import {APP} from 'src/style/variable';
import {CommunicationService} from 'src/service/CommunicationService';
import {
  _ccmResetTimer,
  _updateFollowUpDueDateAndAssignee,
  _updateFollowUpNotes,
  _updateStopTimerNotes
} from '@shared/store/actions/CCMTimerActions';
import RunningTime from '../TimeTracker/RunningTime';
import {useDebouncedCallback} from 'use-debounce';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import moment from 'moment';
// import { allUserOfClinicGroup } from '@shared/services/ClinicServices';
import {State} from '@shared/store/reducers/Index';
import HelperUtils from '@shared/utils/HelperUtils';
import CCMShowFullNotesDialog from '../CCMShowFullNotesDialog/CCMShowFullNotesDialog';
import MultiSelectDropDownComponent from '@components/system/MultiSelectDropdown/MultiSelectDropdown';
import { capitalizeReason } from '../utils/utils';

interface StopTimerDialogProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onSave: () => void;
}

type StateProps = {
  StartDate: string;
  reasonList: Array<any>;
  finalDuration: number;
  buttonLoader: boolean;
  showPreviousFollowUpNoteConfirmDialog: boolean;
  moreDetails: boolean;
  selectedReasons: any;
};

const useStyles = makeStyles(() => ({
  prevFollowUpNotesWrapper: {
    marginBottom: '1.5rem'
  },
  prevFollowUpNote: {
    marginTop: '0.5rem',
    color: '#54585b',
    lineHeight: 1,
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-label': {
      paddingBlock: 9
    }
  },
  markFollowUpNoteAsDoneWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    paddingTop: 0,
    lineHeight: 1
  },
  followUpNotesAndMarkAsDoneWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'space-between',
    marginTop: '0.5rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
    lineHeight: 1
  },
  followUpNotesTitle: {
    marginTop: '1rem'
  },
  followUpWrapper: {
    display: 'flex',
    alignItems: 'baseline'
  },
  followUpDueDate: {
    flex: 1,
    marginRight: '1rem'
  },
  addButton: {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: 'blue',
    textDecoration: 'underline',
    '&:disabled': {
      color: 'gray',
      cursor: 'not-allowed',
      textDecoration: 'none'
    }
  },
  reasonChipStyle: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  },
  reasonsInputField: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }
}));

const StopTimerDialog: React.FC<StopTimerDialogProps> = ({
  open,
  onClose,
  onCancel,
  onSave
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [state, setState] = useState<StateProps>({
    StartDate: CCMEnrollementDialogUtils.CurrentDate(),
    reasonList: [],
    finalDuration: 0,
    buttonLoader: false,
    showPreviousFollowUpNoteConfirmDialog: false,
    moreDetails: false,
    selectedReasons: []
  });
  const {
    reasonList,
    buttonLoader,
    showPreviousFollowUpNoteConfirmDialog,
    moreDetails,
    selectedReasons
  } = state;
  const timerInfo = useSelector((stateParam: any) => stateParam.ccmTimer);
  // eslint-disable-next-line
  const groupId = useSelector((state: State) => state.auth.groupId);
  const [selectedAssignee, setSelectedAssignee] = React.useState(
    timerInfo?.followUpDueDateAndAssigneeObj?.followUpAssignee?.label || ''
  ); // To manage selected assignee
  const [ccmUsers, setCcmUsers] = useState([]);
  const [reasonNameChips, setReasonNameChips] = useState<string[]>([]);

  const {control, handleSubmit, errors, setValue, watch, getValues} = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      const error: any = {};
      // destructure the field values and map them to validate
      const {reason_name: reasonName} = values;
      // Check if `selectedReasons` is an array and has at least one element
      if (!Array.isArray(selectedReasons) || selectedReasons.length === 0) {
        error.activityReasons = t('CCM.CCM_REASON');
      }
      // Validate reason_name if "Specify Custom Task" is selected
      if (
        Array.isArray(selectedReasons) &&
        selectedReasons.includes(CCMConstants.SPECIFY_CUSTOM_TASK)
      ) {
        const newReason: string = reasonName?.trim();
        // Check if no chips exist and reason_name is empty
        if (reasonNameChips.length === 0 && !newReason) {
          error.reason_name = t('CCM.CCM_REASON_NAME');
        }

        // Check if reason_name already exists in chips
        if (newReason && reasonNameChips.includes(newReason)) {
          error.reason_name = `"${newReason}" is already added.`;
        }
      }

      if (values?.follow_up_notes_due_date) {
        if (!moment(values?.follow_up_notes_due_date).isValid()) {
          error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DATE';
        } else if (
          moment(values?.follow_up_notes_due_date).isBefore(
            moment().startOf('day')
          )
        ) {
          error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DUE_DATE';
        }
      }

      return {
        values,
        errors: error
      };
    },
    defaultValues: {
      notes: timerInfo?.stopTimerNotes,
      follow_up_notes: timerInfo?.followUpNotes,
      follow_up_notes_due_date:
        timerInfo?.followUpDueDateAndAssigneeObj?.followUpDueDate || null,
      follow_up_notes_assignee:
        timerInfo?.followUpDueDateAndAssigneeObj?.followUpAssignee?.id || null,
      selectedReasons: [] // Initialize selectedReasons as an empty array
    }
  });
  const {
    startTime: selectedCMMPatientStartTime
    // stopTime: selectedCMMPatientStopTime
  } = timerInfo;
  let localStorageData: any = Storage.getItem(
    CCMConstants.STORAGE_CCM_TIMER_DATA
  );
  if (localStorageData) {
    localStorageData = JSON?.parse?.(localStorageData);
  }

  // const selectedCMMStopTime = selectedCMMPatientStopTime && Date.now();
  const init = async () => {
    try {
      setState(prevState => ({
        ...prevState,
        buttonLoader: true
      }));
      const res = await getCCMLogReasons(localStorageData.groupId);
      // Capitalize reasons once when we receive the list so UI and payload use the same casing
      const capitalizedReasonList =
      Array.isArray(res.data) && res.data.length
        ? res.data.map((item: any) => ({
            ...item,
            reason:
              typeof item.reason === 'string'
                ? capitalizeReason(item.reason)
                : item.reason
          }))
        : [];
      setState(prevState => ({
        ...prevState,
        reasonList: capitalizedReasonList,
        buttonLoader: false
      }));
    } catch (e) {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
      enqueueSnackbar(e && SnackBarConfig('e'));
    }
  };

  // useEffect(() => {
  //   const timeDiff =
  //     (selectedCMMStopTime || 0) - (selectedCMMPatientStartTime || 0);

  //   const timeDiffInSeconds = Math.floor(timeDiff / 1000);
  //   setState(prevState => ({
  //     ...prevState,
  //     finalDuration: timeDiffInSeconds
  //   }));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedCMMPatientStopTime, selectedCMMPatientStartTime]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();

  const notesUpdateDebounced = useDebouncedCallback(notes => {
    dispatch(_updateStopTimerNotes(notes));
  }, SEARCH_DEBOUNCE_DELAY);

  const followUpNotesUpdateDebounced = useDebouncedCallback(followUpNotes => {
    dispatch(_updateFollowUpNotes(followUpNotes));
  }, SEARCH_DEBOUNCE_DELAY);

  const {notes: formNotes, follow_up_notes: formFollowUpNotes} = watch([
    'notes',
    'follow_up_notes'
  ]);

  useEffect(() => {
    notesUpdateDebounced(formNotes);
  }, [formNotes, notesUpdateDebounced]);

  useEffect(() => {
    followUpNotesUpdateDebounced(formFollowUpNotes);
  }, [followUpNotesUpdateDebounced, formFollowUpNotes]);

  const handleStopTimer = async (data: any) => {
    const date = new Date();
    const utcDate = date.toUTCString();

    try {
      let timerData: any = Storage.getItem(CCMConstants.STORAGE_CCM_TIMER_DATA);
      timerData = JSON.parse(timerData || null);
      const timeDiff = date.getTime() - (selectedCMMPatientStartTime || 0);

      const timeDiffInSeconds = Math.floor(timeDiff / 1000);
      let trimmedNotes = null;
      if (data.notes && typeof data.notes === 'string' && data.notes.trim()) {
        trimmedNotes = data.notes.trim();
      }
      let trimmedFollowUpNotes = null;
      if (
        data?.follow_up_notes &&
        typeof data.follow_up_notes === 'string' &&
        data.follow_up_notes.trim()
      ) {
        trimmedFollowUpNotes = data.follow_up_notes.trim();
      }
      // ** Creating the Activiy Reasons multiselect dropdown payload formation **//
      // ** Find the matching items from reasonList based on selectedReasons --> START */
      // Step 1: Filter selected reasons from reasonList because SelectedReasons array has only string value doesnt have id's
      const filteredReasons: any = reasonList.filter((value: any) =>
        selectedReasons.includes(value.reason)
      );
      // Step 2: Exclude "Specify Custom Task" from the filtered list--
      // because I dont want to send reason_id : 5 which is exclusively added for specify custom task in multi select dropdown
      const filteredReasonsWithoutCustomTask: any = filteredReasons.filter(
        (value: any) => value.reason !== CCMConstants.SPECIFY_CUSTOM_TASK
      );
      // Step 3: Map the remaining reasons to their IDs
      const reasonIds: any = filteredReasonsWithoutCustomTask.map(
        (resn: any) => ({
          reason_id: resn.id
        })
      );

      // Step 4: Map reasonNameChips to reason_name
      const reasonNames: any = reasonNameChips.map((reasonName: string) => ({
        reason_name: reasonName
      }));

      // Step 5: Combine reasonIds and reasonNames into optedReasons array
      const optedReasons: any = reasonIds.concat(reasonNames);
      const payload: any = {
        // ...data,
        activity_reason: optedReasons,
        stop_time: utcDate,
        time_spent: timeDiffInSeconds,
        log_type: 'message', // TODO: update this log_type value,
        follow_up_notes_due_date: data?.follow_up_notes_due_date
          ? moment(data?.follow_up_notes_due_date).format('YYYY-MM-DD')
          : null,
        follow_up_notes_assignee: data?.follow_up_notes_assignee,
        notes: trimmedNotes,
        follow_up_notes: trimmedFollowUpNotes
      };

      const options: Record<string, any> = {};
      if (timerData?.ccmPanelData?.clinicId) {
        options.clinic_id = timerData?.ccmPanelData?.clinicId;
      }
      const res = await updateStopTimerPatientLog(
        localStorageData.groupId,
        localStorageData.ccmPatientId,
        localStorageData.ccmPatientTimerLogId,
        payload,
        options
      );
      if (res) {
        enqueueSnackbar(
          t('CCM.API_SUCCESS_ERROR_MESSGAGES.UPDATED_RECORD_DEATILS_SUCCESS'),
          SnackBarConfig('s')
        );
        Storage.setItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA,
          JSON.stringify({
            startTime: null,
            stopTime: null,
            isRunning: false,
            patientId: '',
            ccmPatientId: ''
          })
        );
        dispatch(_ccmResetTimer());
        onSave();
        onClose();
        CommunicationService.sendEvent({
          action: 'STOPPED_CCM_TIMER',
          data: null
        });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message, SnackBarConfig('e'));
    }
  };
  const onSubmit = async (data: any) => {
    // If there is follow up notes and the prev follow up note is not marked as done,
    // show confirmation dialog asking whether the previous follow up notes should be marked as done.
    if (
      (data.follow_up_notes ||
        data.follow_up_notes_due_date ||
        data.follow_up_notes_assignee) &&
      // If there is any previous follow up notes
      localStorageData?.ccmPanelData?.prevFollowUpNotesLogId
    ) {
      setState(p => ({
        ...p,
        showPreviousFollowUpNoteConfirmDialog: true
      }));
    } else {
      handleStopTimer(data);
    }
  };
  const renderDialogTitle = () => {
    return (
      <div style={{fontSize: '1.5rem'}}>
        {t('CCM.STOP_TIMER.STOP_CCM_TIMER', {
          patientName: localStorageData.patientName
        })}
        <strong>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</strong>
        {`${localStorageData.patientAccountNumber})`}
      </div>
    );
  };
  const clinicList = useSelector((_state: any) => _state.clinics.list || []);

  const getClinicTimeZone = (_clinicId: string): string => {
    let timezone = '';
    if (clinicList && Array.isArray(clinicList)) {
      const clinic = clinicList.find(c => c.id === _clinicId);
      if (clinic) {
        timezone = clinic?.zone;
      }
    }
    return timezone;
  };

  const markPrevFollowUpNotesAsDone = async () => {
    try {
      const ccmPanelData = localStorageData.ccmPanelData;
      if (ccmPanelData) {
        const payload = {
          status: FOLLOW_UP_NOTES_LOG_STATUS.COMPLETED
        };
        await updatePrevFollowUpNotesLog(
          ccmPanelData?.groupId,
          ccmPanelData?.ccmPatientId,
          ccmPanelData?.prevFollowUpNotesLogId,
          payload
        );
        const timerDataStr = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        if (timerDataStr) {
          const parsedTimerData = JSON.parse(timerDataStr);
          if (parsedTimerData && parsedTimerData?.ccmPanelData) {
            Storage.setItem(
              CCMConstants.STORAGE_CCM_TIMER_DATA,
              JSON.stringify({
                ...parsedTimerData,
                ccmPanelData: {
                  ...parsedTimerData.ccmPanelData,
                  prevFollowUpNotes: '',
                  prevFollowUpNotesLogId: undefined
                }
              })
            );
          }
          CommunicationService.sendEvent({
            action: 'CCM_PANEL_DATA_UPDATED',
            data: null
          });
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
    }
  };

  const renderPrevFollowUpNotes = () => {
    const prevFollowUpNotes =
      localStorageData?.ccmPanelData?.prevFollowUpNotes || '';
    const maxCharacters = 15;
    return (
      <div className={`${classes.prevFollowUpNotesWrapper}`}>
        <p className={`${classes.header}`}>
          {t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
        </p>
        <div className={`${classes.followUpNotesAndMarkAsDoneWrapper}`}>
          {prevFollowUpNotes.length <= maxCharacters ? (
            <p>{localStorageData?.ccmPanelData?.prevFollowUpNotes}</p>
          ) : (
            <p>
              {prevFollowUpNotes.slice(0, maxCharacters)}
              {'... '}
              <span
                onClick={() => {
                  setState(p => ({
                    ...p,
                    moreDetails: true
                  }));
                }}
                className="link-text-style"
                style={{cursor: 'pointer'}}>
                {t('CCM.MORE_DETAILS')}
              </span>
            </p>
          )}
          <div
            onClick={markPrevFollowUpNotesAsDone}
            className={`link-text-style ${classes.markFollowUpNoteAsDoneWrapper}`}>
            <CheckCircleOutlineIcon />
            <p>{t('CCM.MARK_PREV_FOLLOW_UP_NOTE_AS_DONE')}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderMoreNotesDetails = () => {
    return (
      <CCMShowFullNotesDialog
        open={moreDetails}
        onClose={() => {
          setState(p => ({
            ...p,
            moreDetails: false
          }));
        }}
        notes={localStorageData?.ccmPanelData?.prevFollowUpNotes as string}
        title={t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
      />
    );
  };

  const renderContent = () => {
    return (
      <div style={{marginBottom: '1rem'}}>
        <Grid container direction="row" justify="space-between">
          <Grid item style={{...gridStyle}}>
            <div style={{...spaceBottomStyle}}>
              {t('CCM.STOP_TIMER.START_TIME')}
            </div>
            <div>
              {appointmentsDateTimeFormat(
                localStorageData.startTime,
                getClinicTimeZone(localStorageData?.clinicId)
              ) || '-'}
            </div>
          </Grid>

          <Grid item style={{...gridStyle}}>
            <div style={{...spaceBottomStyle}}>
              {t('CCM.STOP_TIMER.DURATION')}
            </div>

            <div style={{fontSize: '1.1rem'}}>
              {
                <RunningTime
                  startTime={new Date(selectedCMMPatientStartTime)}
                />
              }
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  const renderNotesTextArea = () => {
    return (
      <Controller
        id="notes"
        control={control}
        name={'notes'}
        render={({onChange, value}): React.ReactElement => (
          <TextareaAutosize
            rowsMax={10}
            rows={4}
            aria-label="maximum height"
            placeholder={t('CCM.ENTER_SOME_NOTES')}
            className="app-text-area"
            onChange={onChange}
            value={value}
          />
        )}
      />
    );
  };

  const renderFollowUpNotesTextArea = () => {
    return (
      <Controller
        id="follow_up_notes"
        control={control}
        name={'follow_up_notes'}
        render={({onChange, value}): React.ReactElement => (
          <TextareaAutosize
            rowsMax={10}
            rows={4}
            aria-label="maximum height"
            placeholder={t('CCM.ENTER_FOLLOW_UP_NOTES')}
            className="app-text-area"
            onChange={onChange}
            value={value}
          />
        )}
      />
    );
  };

  const renderButtons = () => {
    return (
      <div>
        <Button
          style={{
            height: '3.25rem',
            width: '14rem',
            fontSize: '1.25rem',
            textTransform: 'capitalize',
            marginRight: '8px'
          }}
          onClick={() => {
            onCancel();
            onClose();
          }}>
          {t('COMMON.CANCEL')}
        </Button>
        <Button
          disabled={
            buttonLoader ||
            (selectedReasons?.includes(CCMConstants.SPECIFY_CUSTOM_TASK) &&
              reasonNameChips?.length === 0)
          }
          style={{
            height: '3.25rem',
            width: '14rem',
            fontSize: '1.25rem',
            textTransform: 'capitalize',
            marginRight: '8px'
          }}
          className="button-style"
          type="submit"
          variant={'contained'}
          onClick={handleSubmit(onSubmit)}
          color="primary">
          {buttonLoader ? (
            <ButtonLoader color={APP.COLOR.DEFAULT.WHITE} />
          ) : (
            t('COMMON.SAVE')
          )}
        </Button>
      </div>
    );
  };

  const renderMarkPrevFollowUpNoteConfirmDialog = () => {
    return (
      <ConfirmationDialog
        open={showPreviousFollowUpNoteConfirmDialog}
        title={t('CCM.CCM_MARK_PREV_FOLLOW_UP_NOTES_DIALOG_TITLE')}
        description={t('CCM.CCM_MARK_PREV_FOLLOW_UP_NOTES_DIALOG_DESCRIPTION')}
        onClose={response => {
          const data = getValues();
          if (response === 'confirm') {
            data.complete_previous_note = true;
          }
          handleStopTimer(data);
          setState(p => ({
            ...p,
            showPreviousFollowUpNoteConfirmDialog: false
          }));
        }}
        cancelButtonText={t('COMMON.NO')}
        confirmButtonText={t('COMMON.YES')}
      />
    );
  };
  const renderFollowUpNotesDueDate = () => {
    return (
      <div className={classes.followUpDueDate}>
        <div
          className={classes.followUpNotesTitle}
          style={{...spaceBottomStyle}}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_DUE_DATE')}
        </div>
        <FormControl
          fullWidth={true}
          variant="outlined"
          error={errors?.follow_up_notes_due_date}>
          <Controller
            control={control}
            error={errors?.follow_up_notes_due_date}
            name="follow_up_notes_due_date"
            render={({onChange, ...inputProps}) => (
              <DateTimePickerWrapper
                style={{width: '100%'}}
                minDate={new Date()}
                autoComplete="on"
                margin={'dense'}
                type={'string'}
                placeholder={t('CCM.DUE_DATE_PLACEHOLDER')}
                {...inputProps}
                onChange={(date, value) => {
                  setValue('follow_up_notes_due_date', value, {
                    shouldValidate: true
                  });
                  dispatch(
                    _updateFollowUpDueDateAndAssignee({
                      followUpDueDate: value as string
                    })
                  );
                }}
              />
            )}
            // defaultValue={null}
          />
          {errors?.follow_up_notes_due_date && (
            <FormHelperText>
              {t(errors.follow_up_notes_due_date)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const handleSelectAssignee = (item: any) => {
    setSelectedAssignee(
      HelperUtils.commaSeparatedFullName(item?.first_name, item?.last_name)
    );
  };
  const hasAssignee = !!watch('follow_up_notes_assignee');
  useEffect(() => {
    if (!hasAssignee) {
      setSelectedAssignee('');
    }
  }, [hasAssignee]);
  const renderFollowUpNotesAssignee = () => {
    return (
      <div style={{flex: 1}}>
        <div
          className={classes.followUpNotesTitle}
          style={{...spaceBottomStyle}}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_ASSIGNEE')}
        </div>
        <FormControl
          fullWidth={true}
          error={!!errors?.follow_up_notes_assignee}
          variant="outlined"
          className="select_clinic">
          <Controller
            id="follow_up_notes_assignee"
            control={control}
            name="follow_up_notes_assignee"
            error={!!errors?.follow_up_notes_assignee}
            render={({onBlur}) => (
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                margin="dense"
                value={selectedAssignee || 'Select Assignee'} // Set selected value in the dropdown
                variant="outlined"
                renderValue={selected => selected as string}
                fullWidth={true}
                onBlur={onBlur}
                name="follow_up_notes_assignee"
                IconComponent={KeyboardArrowDownIcon}>
                <div>
                  {ccmUsers.map((item: any) => (
                    <MenuItem
                      key={item?.id}
                      value={HelperUtils.commaSeparatedFullName(
                        item?.first_name,
                        item?.last_name
                      )}
                      onClick={() => {
                        handleSelectAssignee(item);
                        setValue('follow_up_notes_assignee', item.id, {
                          shouldValidate: true
                        });
                        dispatch(
                          _updateFollowUpDueDateAndAssignee({
                            followUpAssignee: {
                              id: item?.id,
                              label: HelperUtils.commaSeparatedFullName(
                                item?.first_name,
                                item?.last_name
                              )
                            }
                          })
                        );
                      }}>
                      <ListItemText
                        primary={HelperUtils.commaSeparatedFullName(
                          item?.first_name,
                          item?.last_name
                        )}
                      />
                    </MenuItem>
                  ))}
                </div>
              </Select>
            )}
            // defaultValue={null}
          />
          {errors?.follow_up_notes_assignee && (
            <FormHelperText>
              {t(errors.follow_up_notes_assignee)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const renderFollowUpNotesDueDateAndAssignedUserDetails = () => {
    return (
      <div style={{flex: 1}} className={classes.followUpWrapper}>
        {renderFollowUpNotesDueDate()}
        {renderFollowUpNotesAssignee()}
      </div>
    );
  };

  // ** Multi selected dropdown implementation start **//
  useEffect(() => {
    // Check if selectedReasons is empty or not
    if (selectedReasons.length === 0) {
      // If empty, set validation error for 'activityReasons'
      setValue('activityReasons', [], {shouldValidate: true});
    } else {
      // If not empty, clear validation error for 'activityReasons'
      setValue('activityReasons', selectedReasons, {shouldValidate: true});
    }
    if (reasonNameChips?.length > 0) {
      setValue('reason_name', '', {
        shouldValidate: true
      });
    }
  }, [selectedReasons, setValue, reasonNameChips]);

  const handleItemChange = (event: any, clinic: any) => {
    // handler for item change
  };

  const onClickCalled = (event: any, clinic: any) => {
    // Clone the current selectedReasons array
    const activityReasons = [...selectedReasons];
    // Check if the activityReasons is already selected
    const reasonIndex = activityReasons.indexOf(clinic.reason);
    if (reasonIndex > -1) {
      // If activityReasons is already selected, remove it
      activityReasons.splice(reasonIndex, 1);
    } else {
      // If activityReasons is not selected, add it
      activityReasons.push(clinic?.reason);
    }
    // Update the state with the modified reasons array
    setState(prevState => ({
      ...prevState,
      selectedReasons: activityReasons
    }));
  };

  const handleAddChip = (chipValue: string): void => {
    const newReason = chipValue.trim();
    if (newReason && !reasonNameChips.includes(newReason)) {
      setReasonNameChips(prevChips => [...prevChips, newReason]); // Add new chip to array
    }
  };

  const handleDeleteChip = (chipToDelete: string): void => {
    setReasonNameChips((prevChips: any) =>
      prevChips.filter((chip: any) => chip !== chipToDelete)
    );
  };

  const renderPreDefinedActivityReasons = () => {
    return (
      <>
        <div style={{...spaceBottomStyle}}>{t('CCM.REASON')}</div>
        <FormControl
          fullWidth={true}
          error={!!errors?.activityReasons}
          variant="outlined"
          className="select_clinic">
          <Controller
            name="activityReasons"
            control={control}
            // defaultValue={[]}
            render={field => (
              <MultiSelectDropDownComponent
                selectedValues={selectedReasons}
                data={reasonList}
                onClickCalled={(event: any, clinic: any) => {
                  onClickCalled(event, clinic);
                  field.onChange(selectedReasons); // Update the field value
                }}
                handleItemChange={(event: any, clinic: any) => {
                  handleItemChange(event, clinic);
                }}
                clearFilter={() => {
                  setState((p: any) => ({
                    ...p,
                    selectedReasons: []
                  }));
                  field.onChange([]);
                }}
                primaryLabel={
                  selectedReasons.length
                    ? selectedReasons
                    : [t('Select the activity/reason')]
                }
                headerLabel={t('Activity/Reason')}
                objKey={'reason'}
              />
            )}
          />
          {errors?.activityReasons && (
            <FormHelperText>{t(errors.activityReasons)}</FormHelperText>
          )}
        </FormControl>
      </>
    );
  };

  const renderSpecifyCustomTaskField = () => {
    return (
      <div>
        {selectedReasons.includes(CCMConstants.SPECIFY_CUSTOM_TASK) && (
          <div>
            <div style={{...spaceBottomStyle}}>
              {t('CCM.STOP_TIMER.REASON_NAME')}*
            </div>
            <FormControl
              fullWidth={true}
              variant="outlined"
              error={!!errors?.reason_name}
              style={{paddingBottom: '0'}}>
              <Controller
                name="reason_name"
                control={control}
                defaultValue=""
                render={({value, onChange}) => (
                  <div className={classes.reasonsInputField}>
                    <StyledOutlinedInput
                      style={{flex: 1}}
                      value={value || ''}
                      onChange={event => {
                        onChange(event.target.value);
                      }}
                      placeholder={t('CCM.STOP_TIMER.ENTER_REASON_NAME')}
                      autoComplete="on"
                    />
                    <Link
                      component="button"
                      // variant="body2"
                      className={classes.addButton}
                      onClick={(event: any) => {
                        event.preventDefault();
                        handleAddChip(value); // Add chip
                        onChange(''); // Clear input field
                      }}
                      disabled={!value?.trim()}>
                      {t('Add')}
                    </Link>
                  </div>
                )}
              />
              {errors?.reason_name && (
                <FormHelperText>{t(errors.reason_name)}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}
      </div>
    );
  };

  const renderDialogContent = () => {
    return (
      <div>
        {renderContent()}
        <div>
          {renderPreDefinedActivityReasons()}
          {renderSpecifyCustomTaskField()}
          {/* Render Reason name Chips */}
          <div className={classes.reasonChipStyle}>
            {reasonNameChips.map((chip: any, index: number) => (
              <Chip
                key={index}
                label={chip}
                onDelete={() => {
                  handleDeleteChip(chip);
                }}
                style={{margin: '0.2rem'}}
              />
            ))}
          </div>
        </div>
        {localStorageData?.ccmPanelData?.prevFollowUpNotes &&
          renderPrevFollowUpNotes()}
        <div style={{...spaceBottomStyle}}>{`${t('CCM.NOTES')}`}</div>
        {renderNotesTextArea()}
        <div
          className={classes.followUpNotesTitle}
          style={{...spaceBottomStyle}}>
          {`${t('CCM.FOLLOW_UP_NOTES')}`}
        </div>
        {renderFollowUpNotesTextArea()}
        {renderFollowUpNotesDueDateAndAssignedUserDetails()}
        {showPreviousFollowUpNoteConfirmDialog &&
          renderMarkPrevFollowUpNoteConfirmDialog()}
        {moreDetails && renderMoreNotesDetails()}
      </div>
    );
  };

  // TODO: Replace this with the redux value once the patient shared redux state issue is fixed
  const {userPermissions} = useSelector((_state: State) => _state.auth);
  let patientShared = false;
  if (groupId) {
    patientShared =
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === true;
  }
  const fetchCCMUsersList = async () => {
    try {
      // eslint-disable-next-line camelcase
      const options: {clinic_id?: number | string} = {};
      if (!patientShared) {
        options['clinic_id'] = localStorageData?.clinicId;
      }
      const response = await getAllCCMUsersOfClinicGroup(groupId, options);
      setCcmUsers(response?.data?.rows);
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) || t('CLINICS.CLINIC_GROUP_USER_ERROR'),
        SnackBarConfig('e')
      );
      throw error;
    }
  };

  useEffect(() => {
    fetchCCMUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Dialog
      disableEnforceFocus
      maxWidth={'sm'}
      fullWidth={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      open={open}
      onClose={onClose}
      aria-labelledby="enrollment-dialog-title">
      <DialogTitle disableTypography id="enrollment-dialog-title">
        {renderDialogTitle()}
      </DialogTitle>
      <DialogContent dividers>{renderDialogContent()}</DialogContent>
      <DialogActions>{renderButtons()}</DialogActions>
    </Dialog>
  );
};

export default StopTimerDialog;
